import React, { useState, useEffect } from 'react';
import { makeStyles } from 'utils/makeStylesAdapter';
import {
  Grid,
  Box,
  TextareaAutosize,
  Typography,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { InputConsensus, ButtonConsensus, KeyboardDatePicker, ModalConfirm } from 'components';
import { colors } from 'utils/colorsSteps';
import { toast } from 'react-toastify';
import { formatDateToISO8601 } from 'utils/date-time.utils';
import moment from 'moment';
import { parcelsAmountSum } from 'views/Agreement/util';
import { orderParcelsByInitialDate } from 'utils/parcels';
import { PARTNER_SITUATIONS } from 'constants/partner-situations';
import { initialDate } from 'utils/DatesInMonth';
import { PENALTY_KINDS } from 'constants/agreement/penaltyKinds';
import LazyAutocomplete from 'components/LazyAutocomplete';
import projectService from 'services/project/projectService';
import materialService from 'services/material-service/materialService';
import associationMaterialService from 'services/association-material/associationMaterialService';
import { AutoCompleteConfig } from 'constants/autocomplete';
import { useParameterKeys } from 'hooks/useParameterKeys';

const useStyles = makeStyles(theme => ({
  containerForm: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    margin: theme.spacing(2, 0, 2, 0),
    width: '100%',
  },
  containerCheck: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerParcels: {
    padding: theme.spacing(2),
    backgroundColor: '#ececec',
    borderRadius: '0.7rem',
    margin: theme.spacing(0, 0.8, 1, 0.8),
  },
}));

const MaterialsForAssociations = props => {
  const {
    setDisabledActionsPenaltyTable = {},
    selectedIndex = null,
    setSelectedIndex = {},
    materials,
    setMaterials,
    description,
    setDescription,
    institutionType,
    institutionSelected,
    judicialDistrictId,
    appliedInstituteFilterKey = null,
  } = props;

  const { isMPDFT } = useParameterKeys();

  const classes = useStyles();

  const [row, setRow] = useState(null);
  const [key, setKey] = useState(false);
  const [key2, setKey2] = useState(false);

  const [openDeleteParcelModalConfirm, setOpenDeleteParcelModalConfirm] = useState(false);
  const [parcelToDelete, setParcelToDelete] = useState(null);

  const initialValuesCompareAmount = {
    amount: 0,
    attendedAmount: 0,
    remainingAmount: 0,
  };

  const [compareAmount, setCompareAmount] = useState({ ...initialValuesCompareAmount });

  const initialValues = {
    relation: null,
    material: null,
    amount: '',
    penalty: PENALTY_KINDS.MATERIALS,
    description: isMPDFT ? null : '',
    deliveryDeadline: '',
    descriptionLocal: isMPDFT ? '' : description,
    parcels: [],
    numberOfParcels: 1,
    project: '',
  };

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...initialValues },
    touched: {},
    errors: {},
  });

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values:
        selectedIndex !== null
          ? {
              ...materials[selectedIndex.index],
              descriptionLocal: isMPDFT ? materials[selectedIndex.index].description : description,
            }
          : { ...initialValues },
    }));
  }, [selectedIndex]);

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        descriptionLocal: description,
      },
    }));
  }, [description]);

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        project: null,
        relation: null,
      },
      touched: {
        ...formState.touched,
        relation: true,
      },
    }));
  }, [institutionSelected]);

  const handleInputKeyPress = event => {
    if (event.key === ',' || event.key === '.') {
      event.preventDefault();
    }
  };

  const handleChange = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
    if (event.target.name === 'descriptionLocal') {
      setFormState(formState => ({
        ...formState,
        values: {
          ...formState.values,
          description: event.target.value,
        },
      }));
    }
  };

  const handleChangeAssociation = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        project: null,
        relation: newValue,
      },
      touched: {
        ...formState.touched,
        relation: true,
      },
    }));

    setCompareAmount(compare => ({
      ...compare,
      amount: newValue ? newValue.amount : 0,
      attendedAmount: newValue ? newValue.attendedAmount : 0,
      remainingAmount: newValue ? newValue.remainingAmount : 0,
    }));

    setKey(!key);
  };

  const handleChangeMaterial = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        material: newValue,
        project: null,
        relation: {
          ...formState.values.relation,
          association: null,
        },
      },
      touched: {
        ...formState.touched,
        material: true,
      },
    }));
    setKey2(!key2);
  };

  const handleClearFormStat = () => {
    setRow(null);
    if (selectedIndex !== null) {
      setDisabledActionsPenaltyTable(prevState => ({
        ...prevState,
        [selectedIndex.penaltiesIndex]: { ...prevState[selectedIndex.penaltiesIndex], edit: false },
      }));
      setSelectedIndex(null);
    }
    setFormState(formState => ({
      ...formState,
      values: { ...initialValues },
    }));
  };

  useEffect(() => {
    if (row) {
      setFormState(formState => ({
        ...formState,
        values: { ...row, descriptionLocal: isMPDFT ? row.description : description },
      }));
      setKey2(!key2);
      setKey(!key);
    }
  }, [row]);

  const handleSubmit = event => {
    event.preventDefault();

    const { material, relation, amount, descriptionLocal, parcels } = formState.values;

    if (!material) {
      toast.warn('Selecione o material');
      return;
    }

    if (institutionType === 'final' && !relation?.association) {
      toast.warn('Selecione a instituição');
      return;
    }

    if (parcels.some(parcel => !parcel.amount)) {
      toast.warn('Insira a quantidade em todas as parcelas');
      return;
    }

    if (parcels.some(parcel => !parcel.date)) {
      toast.warn('Insira a data em todas as parcelas');
      return;
    }

    if (!isMPDFT && descriptionLocal === '') {
      toast.warn('Descreva como será cumprido');
      return;
    }

    if (parcels.length === 0) {
      toast.warn('É necessário adicionar ao menos uma parcela');
      return;
    }

    let intAmount = parseInt(amount);

    if (selectedIndex !== null) {
      let newArray = [...materials];
      let newMaterials = formState.values;
      setDescription(newMaterials.descriptionLocal);
      delete newMaterials.descriptionLocal;
      newArray[selectedIndex.index] = newMaterials;
      setMaterials(newArray);
    } else {
      let newMaterials = formState.values;
      setDescription(newMaterials.descriptionLocal);
      delete newMaterials.descriptionLocal;
      setMaterials([...materials, newMaterials]);
    }

    handleClearFormStat();
  };

  const handleToRead = () => {
    handleClearFormStat();
  };

  const handleAddParcel = () => {
    const { deliveryDeadline, amount, numberOfParcels } = formState.values;

    if (!deliveryDeadline) {
      toast.warn('Insira a data inicial');
      return;
    }
    if (!amount) {
      toast.warn('Insira a quantidade de materiais');
      return;
    }
    if (!numberOfParcels) {
      toast.warn('Insira a quantidade de parcelas');
      return;
    }
    const newParcels = [];
    let date = deliveryDeadline && moment(deliveryDeadline);

    for (let i = 0; i < numberOfParcels; i++) {
      newParcels.push({
        date: date && formatDateToISO8601(date.clone().add(i, 'months')),
        amount: amount,
      });
    }

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        parcels: newParcels,
        deliveryDeadline: '',
        amount: '',
      },
    }));
  };

  const handleChangeProject = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        project: newValue,
      },
    }));
  };

  const handleParcelDateChange = (index, newDate) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index ? { ...parcel, date: newDate } : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleParcelAmountChange = (index, amount) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index ? { ...parcel, amount } : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleOpenDeleteParcelModalConfirm = parcel => {
    setOpenDeleteParcelModalConfirm(true);
    setParcelToDelete(parcel);
  };

  const handleCloseDeleteParcelModalConfirm = () => {
    setOpenDeleteParcelModalConfirm(false);
    setParcelToDelete(null);
  };

  const deleteParcel = () => {
    const parcel = parcelToDelete;
    const index = formState.values.parcels.findIndex(item => item.date === parcel.date);
    if (index !== -1) {
      const newParcels = formState.values.parcels.filter(
        (value, _index, _arr) => value.date !== parcel.date,
      );
      setFormState(formState => ({
        ...formState,
        values: {
          ...formState.values,
          parcels: newParcels,
        },
      }));
    }
  };

  const fetchMaterials = async () => {
    const params = {
      hasDemand: true,
    };

    const result = await materialService.getMaterialService(params);

    return {
      data: result.data.data,
    };
  };

  const fetchAssociations = async (search, page) => {
    const params = {
      active: true,
      forward: false,
      includeAssociationsWithNullSituation: true,
      judicialDistrictId: judicialDistrictId,
      name: search,
      page,
      size: AutoCompleteConfig.PAGINATION_SIZE,
    };

    if (isMPDFT) {
      delete params.includeAssociationsWithNullSituation;
      params.situation = PARTNER_SITUATIONS.ACCREDITED;
      delete params.judicialDistrictId;
    }

    if (formState.values.material?.id) {
      params.materialId = formState.values.material.id;
    }

    if (appliedInstituteFilterKey && isMPDFT) {
      params[appliedInstituteFilterKey] = true;
    }

    const result = await associationMaterialService.getAssociationMaterial(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  const fetchProjects = async (search, page) => {
    const params = {
      associationId: formState.values.relation?.association?.id,
      onlyActiveProjects: true,
      name: search,
      page,
      size: AutoCompleteConfig.PAGINATION_SIZE,
    };

    const result = await projectService.getProjectsService(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  useEffect(() => {
    setFormState(formState => {
      return {
        ...formState,
        values: {
          ...formState.values,
          project: formState.values.project,
        },
      };
    });
  }, [formState.values.relation]);

  return (
    <Box className={classes.container}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1} className={classes.containerForm}>
          <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
            <LazyAutocomplete
              autoHighlight
              onChange={handleChangeMaterial}
              value={formState.values.material}
              fetchOptions={fetchMaterials}
              getOptionLabel={option => option.name || ''}
              enablePagination
              renderInput={params => (
                <InputConsensus
                  {...params}
                  name="materialId"
                  label="Material*"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.loading ? (
                          <CircularProgress color="primary" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
            {formState.values.material && institutionType === 'final' && (
              <LazyAutocomplete
                value={formState.values.relation}
                getOptionLabel={option => option.association?.name}
                onChange={handleChangeAssociation}
                fetchOptions={fetchAssociations}
                enablePagination
                refreshKey={
                  isMPDFT
                    ? `${formState.values.material?.id}-${appliedInstituteFilterKey ?? 'none'}`
                    : formState.values.material?.id
                }
                renderInput={params => (
                  <InputConsensus
                    {...params}
                    name="relation"
                    label={`Instituição${institutionType === 'final' ? '*' : ''}`}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            {formState.values.relation && institutionType === 'final' && (
              <Box marginBottom={3}>
                <LazyAutocomplete
                  value={formState.values.project}
                  getOptionLabel={option => option?.name || ''}
                  onChange={handleChangeProject}
                  fetchOptions={fetchProjects}
                  enablePagination
                  refreshKey={formState.values.relation?.association?.id}
                  renderInput={params => (
                    <InputConsensus
                      {...params}
                      name="project"
                      label="Projeto"
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.loading ? (
                              <CircularProgress color="primary" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Grid>

          <Grid
            container
            spacing={2}
            className={classes.containerParcels}
            size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Typography align="left" variant="h5" className={classes.form}>
                Parcelas
              </Typography>
            </Grid>

            {formState.values.parcels.length === 0 && (
              <>
                <Grid size={{ xl: 5, lg: 5, md: 5, sm: 5, xs: 12 }}>
                  <KeyboardDatePicker
                    variant="inline"
                    name="deliveryDeadline"
                    label="Data inicial*"
                    value={formState.values.deliveryDeadline || null}
                    minDate={!selectedIndex ? initialDate() : undefined}
                    onChange={newDate =>
                      setFormState(formState => ({
                        ...formState,
                        values: {
                          ...formState.values,
                          deliveryDeadline: newDate,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xl: 5, lg: 5, md: 5, sm: 6, xs: 12 }}>
                  <InputConsensus
                    label="Materiais por mês*"
                    name="amount"
                    variant="standard"
                    type="number"
                    inputProps={{
                      min: 0,
                    }}
                    onChange={handleChange}
                    onKeyPress={handleInputKeyPress}
                    value={formState.values.amount || ''}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {formState.values.parcels && formState.values.parcels.length === 0 && (
              <>
                <Grid size={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 12 }}>
                  <InputConsensus
                    label="Número de meses*"
                    name="numberOfParcels"
                    variant="standard"
                    type="number"
                    inputProps={{
                      min: 1,
                    }}
                    onChange={handleChange}
                    onKeyPress={handleInputKeyPress}
                    value={formState.values.numberOfParcels || 1}
                    fullWidth
                  />
                </Grid>
                <Grid
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                  size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                  <ButtonConsensus
                    title="Gerar Parcelas"
                    backgroundColor={colors.primary}
                    hovercolor={colors.primary}
                    onClick={() => handleAddParcel()}
                    size="medium"
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <TableContainer component={Paper}>
              <Table className={classes.table} size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Data</TableCell>
                    <TableCell align="center">Quantidade</TableCell>
                    <TableCell align="center" width={150}>
                      <Box display="flex" position="relative" justifyContent="center">
                        Ações
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderParcelsByInitialDate(formState.values.parcels).map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell component="th" scope="row" align="center">
                        <KeyboardDatePicker
                          variant="inline"
                          name="deliveryDeadline"
                          value={item.date || null}
                          onChange={newDate => handleParcelDateChange(index, newDate)}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row" align="center">
                        <InputConsensus
                          name="amount"
                          variant="standard"
                          type="number"
                          inputProps={{
                            min: 0,
                          }}
                          onKeyPress={handleInputKeyPress}
                          onChange={e => handleParcelAmountChange(index, e.target.value)}
                          value={item.amount || ''}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDeleteParcelModalConfirm(item)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell align="left" colSpan={3} style={{ fontWeight: 'bold' }}>
                      Total: {parcelsAmountSum(formState.values.parcels)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>
          <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }} />
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <TextareaAutosize
              style={{ width: '100%', padding: 5, resize: 'none' }}
              maxRows={4}
              minRows={4}
              aria-label="maximum height"
              placeholder={isMPDFT ? 'Observação' : 'Descrever como será cumprido*'}
              onChange={handleChange}
              name="descriptionLocal"
              value={isMPDFT ? formState.values.description : formState.values.descriptionLocal}
            />
          </Grid>
          <Grid
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Alterar' : 'Incluir'} ${
                isMPDFT ? 'Medida' : 'Penalidade'
              }`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              type="submit"
              size="medium"
            />
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Cancelar' : 'Limpar'}`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              onClick={() => handleToRead()}
              size="medium"
              style={{ marginLeft: '-6px' }}
            />
          </Grid>
        </Grid>
      </form>
      {openDeleteParcelModalConfirm && (
        <ModalConfirm
          open={openDeleteParcelModalConfirm}
          handleClose={handleCloseDeleteParcelModalConfirm}
          onClick={deleteParcel}
          title="Excluir parcela"
        />
      )}
    </Box>
  );
};

export default MaterialsForAssociations;
